"use client";

/**
 * Cart 도메인 TanStack Query 훅
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { createCartService, type CartEntry } from "@/shared/api/services/cartService";
import type { ApiResponse } from "@/shared/api/types";
import { useAuth, useBuyerService } from "@/shared/auth/hooks";

import { cartKeys } from "@/features/cart/queries/keys";

export function useCarts() {
  const { isAuthenticated } = useAuth();
  const service = useBuyerService(createCartService);

  return useQuery({
    queryKey: cartKeys.list(),
    queryFn: () => service.getCarts(),
    enabled: isAuthenticated,
  });
}

export function applyConfirmedCartQuantity(
  response: ApiResponse<CartEntry[]> | undefined,
  variables: { cartId: number; goodsCount: number },
): ApiResponse<CartEntry[]> | undefined {
  if (!response) return response;
  return {
    ...response,
    data: response.data.map((entry) =>
      entry.cart.id === variables.cartId
        ? { ...entry, cart: { ...entry.cart, goodsCount: variables.goodsCount } }
        : entry,
    ),
  };
}

export function useUpdateCartItem() {
  const queryClient = useQueryClient();
  const service = useBuyerService(createCartService);

  return useMutation({
    mutationFn: ({ cartId, goodsCount }: { cartId: number; goodsCount: number }) =>
      service.updateCartItem(cartId, { goodsCount }),
    onSuccess: (_response, variables) => {
      // The PUT succeeded, so immediately align the cached cart with the
      // server-confirmed quantity. This also keeps checkout correct if the
      // follow-up refetch fails.
      queryClient.setQueryData<ApiResponse<CartEntry[]>>(cartKeys.list(), (current) =>
        applyConfirmedCartQuantity(current, variables),
      );

      // Keep the mutation pending until the normal fresh-cart refetch ends.
      return queryClient.invalidateQueries({ queryKey: cartKeys.all });
    },
    onError: () => {
      toast.error("수량 변경에 실패했습니다.");
    },
  });
}

export function useDeleteCartItems() {
  const queryClient = useQueryClient();
  const service = useBuyerService(createCartService);

  return useMutation({
    mutationFn: (cartIds: string[]) => service.deleteMultipleCarts({ cartIds }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: cartKeys.all });
      toast.success("선택한 상품이 삭제되었습니다.");
    },
    onError: () => {
      toast.error("삭제에 실패했습니다.");
    },
  });
}
